import React, { useState, useEffect } from 'react';
import Auth from '@Services/Auth';
import Entries from '@Services/Entries';
import ProfileSettings from '@Services/ProfileSettings';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import find from 'lodash/find';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import head from 'lodash/head';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import { PreferencesSectionsMapping } from '@Pages/ProfileSettings/ProfileSettingsConstants';
import UserInterfaceConfig from '@Services/UserInterfaceConfig';
import { setImmutable } from '@Pages/ProfileSettings/Preferences/utils/setImmutable';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { useStateWithObserver } from './utils/useStateWithObserver';
import PreferencesDefaultSection from './components/sections/PreferencesDefaultSection/PreferencesDefaultSection';
import PreferencesResultsPerPageSection from './components/sections/PreferencesResultsPerPageSection/PreferencesResultsPerPageSection';
import PreferencesTextAndContrastSection from './components/sections/PreferencesTextAndContrastSection/PreferencesTextAndContrastSection';
import PreferencesSynthesisPlannerSection from './components/sections/PreferencesSynthesisPlannerSection/PreferencesSynthesisPlannerSection';
import PreferencesPreferredSuppliersSection from './components/sections/PreferencesPreferredSuppliersSection/PreferencesPreferredSuppliersSection';

const spinnerId = 'profile-settings-profile';

const buildRxEditorOptions = (editUser) => {
  const EDITOR_OPTIONS_KEY = 'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS';
  const separatedOptionsKeys = ['RX2_KEEP_FRAGMENTS', 'RX2_IGNORE_ATOM_MAPPING'];

  const editorOptions = {
    title: 'Include',
    options: [],
  };
  const separatedOptions = {
    options: [],
  };

  const combinedInfo = ProfileSettings.getCombinedInfo();
  const optionsObject = ProfileSettings.getModelValue(combinedInfo, EDITOR_OPTIONS_KEY).preferences;

  Object.keys(optionsObject).forEach((option) => {
    const fullKey = `${EDITOR_OPTIONS_KEY}.${option}`;
    const mappedOption = {
      label: optionsObject[option].label,
      property: option,
      id: `editor_option_${option}`,
      value: ProfileSettings.getModelValue(editUser, fullKey),
    };

    if (optionsObject[option].type === 'check_radio') {
      mappedOption.type = 'check_radio';
      mappedOption.subOptions = optionsObject[option].values;
      mappedOption.modelValue = mappedOption.value;
      mappedOption.value = mappedOption.modelValue !== '';
      mappedOption.modelValue = mappedOption.modelValue || head(mappedOption.subOptions).value;
    }

    if (separatedOptionsKeys.includes(option)) {
      separatedOptions.options.push(mappedOption);
    } else {
      editorOptions.options.push(mappedOption);
    }
  });

  return [editorOptions, separatedOptions];
};

const updateUserPageSize = (pageSize) => {
  const currentUser = Auth.getUser();

  Entries.setLimit(parseInt(pageSize, 10));
  Entries.params = null;

  currentUser.details.settings.pageSize = Entries.limit;
  Auth.updateUser(currentUser);
};

const updateCurrentEditor = (editor, setEditorType) => {
  const currentUser = Auth.getUser();

  if (get(currentUser, 'structureEditor.current')) {
    currentUser.structureEditor.current = null;
  }

  currentUser.details.settings.structureEditorPreselected = editor;

  setEditorType(undefined);

  Auth.updateUser(currentUser);
};

const updateConditionsPosition = (position, alwaysShown) => {
  const currentUser = Auth.getUser();
  let update = false;

  if (get(currentUser, 'details.settings.synthesisPlannerConditionsPosition') !== undefined) {
    currentUser.details.settings.synthesisPlannerConditionsPosition = position;
    update = true;
  }
  if (get(currentUser, 'details.settings.synthesisPlannerConditionsAlwaysShown') !== undefined) {
    currentUser.details.settings.synthesisPlannerConditionsAlwaysShown = alwaysShown;
    update = true;
  }

  if (update) {
    Auth.updateUser(currentUser);
  }
};

/* eslint-disable react/prop-types */
function Preferences({
  // props from Redux
  setEditorType,
  showSupplierPreferencesChangedToastMessage,
  openSupplierPreferences,
}) {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    RouteChangeTracking.trackOnRouteChangeSuccess();
  }, []);

  // states that might be used across entire profile settings
  // need to notify the enclosing profile-settings controller about the changes
  const [originalUser, setOriginalUser] = useStateWithObserver(() => cloneDeep(ProfileSettings.get()));
  const [editUser, setEditUser] = useStateWithObserver(() => cloneDeep(ProfileSettings.get()));

  const [rxEditorOptions, setRxEditorOptions] = useState(() => buildRxEditorOptions(editUser));

  const updatePreferencesSection = (sectionName, closeCallback) => {
    const combinedInfo = ProfileSettings.getCombinedInfo();

    const mergeStaticAndDynamicPayloadProps = () => {
      switch (sectionName) {
        case 'PREDICTION_PLAN': {
          const librariesKey = 'PREDICTION_PLAN.RX2_PREDICTION_PLAN_LIBRARIES';
          const libPrefs = ProfileSettings.getModelValue(combinedInfo, librariesKey)?.preferences || {};
          const libFullKeys = Object.keys(libPrefs).map((name) => `${librariesKey}.${name}`);
          return [
            ...PreferencesSectionsMapping[sectionName].filter((pref) => !pref.includes('LIBRARIES')),
            ...libFullKeys.filter((key) => ProfileSettings.getModelValue(editUser, key) != null),
          ];
        }
        case 'PREDICTION_PLAN_IKTOS': {
          const librariesKey = 'PREDICTION_PLAN_IKTOS.RX2_PREDICTION_PLAN_LIBRARIES';
          const libPrefs = ProfileSettings.getModelValue(editUser, librariesKey) || {};
          const libFullKeys = Object.keys(libPrefs).map((name) => `${librariesKey}.${name}`);
          return [
            ...PreferencesSectionsMapping[sectionName].filter((pref) => !pref.includes('LIBRARIES')),
            ...libFullKeys.filter((key) => ProfileSettings.getModelValue(editUser, key) != null),
          ];
        }
        case 'PREFERRED_SUPPLIERS': {
          const checkedSuppliersMap = get(editUser, ['SUPPLIERS', 'RX2_SUPPLIERS']);
          return Object.keys(checkedSuppliersMap).map((name) => ['SUPPLIERS', 'RX2_SUPPLIERS', name]);
        }
        default:
          return PreferencesSectionsMapping[sectionName];
      }
    };

    const payload = [];
    const payloadProps = mergeStaticAndDynamicPayloadProps();
    const newFontColor = get(editUser, 'PAGE_STYLE.RX2_PAGE_STYLE_TEXT_COLOR');
    const newFontSize = get(editUser, 'PAGE_STYLE.RX2_PAGE_STYLE_TEXT_SIZE_RELATIVE');
    const currentFontSize = get(originalUser, 'PAGE_STYLE.RX2_PAGE_STYLE_TEXT_SIZE_RELATIVE');
    const newContrast = get(editUser, 'PAGE_STYLE.RX2_PAGE_STYLE_TEXT_CONTRAST');
    const isRCSUnchecked = get(editUser, 'PREDICTION_PLAN_IKTOS.isRCSUnchecked');

    const trackingList = [];
    setIsLoading(true);
    payloadProps.forEach((payloadProp) => {
      const combinedItem = ProfileSettings.getModelValue(combinedInfo, payloadProp) || {};

      const name = ProfileSettings.getPropertyName(payloadProp);
      const value = ProfileSettings.getModelValue(editUser, payloadProp);
      const readableName = combinedItem.label;
      const readableValue = get(find(combinedItem.values, { value }), 'label', value);
      const payloadItem = {
        name,
        value: isUndefined(value) || isNull(value) ? '' : value,
      };
      const trackingItem = {
        name,
        value,
        readableName,
        readableValue,
        isInclude: !!find(rxEditorOptions[0].options, { property: payloadProp }),
      };
      if (isRCSUnchecked) {
        if (payloadItem.name === 'RCS10D') payloadItem.value = false;
        if (payloadItem.name === 'RCSANY') payloadItem.value = false;
        if (payloadItem.name === 'RX2_PREDICTION_PLAN_IKTOS_PRICE') payloadItem.value = '';
      }
      payload.push(payloadItem);
      trackingList.push(trackingItem);
    });

    ProfileSettings.trackProfileSettings(sectionName, trackingList);
    return ProfileSettings.updatePreferences(payload, sectionName)
      .then(() => {
        setOriginalUser(cloneDeep(editUser));
        switch (sectionName) {
          case 'AUTOPLAN':
          case 'SHOW_AUTOPLAN':
            break;
          case 'PREDICTION_PLAN':
            break;
          case 'PREDICTION_PLAN_IKTOS':
            break;
          case 'RESULT_PER_PAGE':
            updateUserPageSize(editUser.RESULT_PER_PAGE.RX2_PAGE_SIZE);
            break;
          case 'DEFAULT_STRUCTURE_EDITOR':
            updateCurrentEditor(editUser.DEFAULT_EDITOR_SETTINGS.RX2_STRUCTURE_EDITOR_PRESELECTED, setEditorType);
            break;
          case 'RX2_PAGE_STYLE_TEXT_SIZE_RELATIVE':
            UserInterfaceConfig.applyUserFontSizeSettings(currentFontSize, newFontSize);
            break;
          case 'RX2_PAGE_STYLE_TEXT_COLOR':
          case 'RX2_PAGE_STYLE_TEXT_CONTRAST':
            UserInterfaceConfig.applySchema(newFontColor, newContrast);
            break;
          case 'RX2_SYNTHESIS_CONDITIONS_POSITION':
            updateConditionsPosition(
              editUser.SYNTHESIS_PLANNER.RX2_SYNTHESIS_CONDITIONS_POSITION,
              editUser.SYNTHESIS_PLANNER.RX2_SYNTHESIS_CONDITIONS_ALWAYS_SHOW
            );
            break;
          case 'PREFERRED_SUPPLIERS': {
            const optionsMap = get(combinedInfo, ['SUPPLIERS', 'preferences', 'RX2_SUPPLIERS', 'preferences']);
            const originalCheckedMap = get(originalUser, ['SUPPLIERS', 'RX2_SUPPLIERS']);
            const checkedMap = get(editUser, ['SUPPLIERS', 'RX2_SUPPLIERS']);
            showSupplierPreferencesChangedToastMessage({ optionsMap, originalCheckedMap, checkedMap });
            break;
          }
          default:
            Logger.warn('ProfileSettings unrecognized section name: ', sectionName);
        }
      })
      .then(() => {
        if (closeCallback) {
          closeCallback();
        }
      })
      .finally(() => {
        setIsLoading(false);
      })
      .catch(Logger.error);
  };

  const resetSectionsToOriginal = (sections) => {
    const newEditUser = cloneDeep(editUser);
    ProfileSettings.migrateSectionValues(sections, originalUser, newEditUser);
    if (sections.includes('PREDICTION_PLAN_IKTOS')) {
      const originalPredictionPlanLibraries = get(originalUser, 'PREDICTION_PLAN_IKTOS.RX2_PREDICTION_PLAN_LIBRARIES');
      newEditUser.PREDICTION_PLAN_IKTOS.RX2_PREDICTION_PLAN_LIBRARIES = cloneDeep(originalPredictionPlanLibraries);
      newEditUser.PREDICTION_PLAN.RX2_PREDICTION_PLAN_LIBRARIES = cloneDeep(originalPredictionPlanLibraries);
    }
    setEditUser(newEditUser);
    return newEditUser;
  };

  const resetSectionsToDefault = (sections) => {
    const defaultModel = ProfileSettings.getDefault();
    const newOriginalUser = cloneDeep(originalUser);
    const newEditUser = cloneDeep(editUser);
    ProfileSettings.migrateSectionValues(sections, defaultModel, newEditUser);
    ProfileSettings.migrateSectionValues(sections, newEditUser, newOriginalUser);

    if (sections.includes('PREDICTION_PLAN')) {
      const combinedInfo = ProfileSettings.getCombinedInfo();
      const librariesKey = 'PREDICTION_PLAN.RX2_PREDICTION_PLAN_LIBRARIES';
      const libPrefs = ProfileSettings.getModelValue(combinedInfo, librariesKey).preferences;
      const libFullKeys = Object.keys(libPrefs).map((name) => `${librariesKey}.${name}`);
      // generate libs defaults
      const libsDefaultModel = Object.keys(libPrefs)
        .map((name) => {
          return { name, value: ProfileSettings.parseDefaultValue(libPrefs[name].values) };
        })
        .reduce((result, v) => {
          // eslint-disable-next-line no-return-assign,no-param-reassign
          result[`${librariesKey}.${v.name}`] = v.value;
          return result;
        }, {});
      ProfileSettings.migratePreferenceValues(libFullKeys, libsDefaultModel, newEditUser);
      ProfileSettings.migratePreferenceValues(libFullKeys, newEditUser, newOriginalUser);
    }
    if (sections.includes('PREDICTION_PLAN_IKTOS')) {
      const combinedInfo = ProfileSettings.getCombinedInfo();
      const librariesKey = 'PREDICTION_PLAN_IKTOS.RX2_PREDICTION_PLAN_LIBRARIES';
      const libPrefs = ProfileSettings.getModelValue(combinedInfo, librariesKey).preferences;
      const libsDefaultModel = Object.keys(libPrefs)
        .map((name) => {
          return { name, value: ProfileSettings.parseDefaultValue(libPrefs[name].values) };
        })
        .reduce((result, v) => {
          // eslint-disable-next-line no-return-assign,no-param-reassign
          result[`${librariesKey}.${v.name}`] = v.value;
          return result;
        }, {});
      Object.keys(libsDefaultModel).forEach((eachKey) => {
        set(newOriginalUser, eachKey, libsDefaultModel[eachKey]);
        set(newEditUser, eachKey, libsDefaultModel[eachKey]);
      });
    }
    setOriginalUser(newOriginalUser);
    setEditUser(newEditUser);

    return [newOriginalUser, newEditUser];
  };
  const resetSections = (sectionName, subsections, closeCallback) => {
    let resetRequestFn;
    // we can reset only one section at one time
    switch (sectionName) {
      case 'DEFAULT_EDITOR_SETTINGS':
        // Reset in React Redux
        setEditorType(undefined);
        resetRequestFn = ProfileSettings.resetPreferencesStructureEditor;
        break;
      case 'AUTOPLAN':
      case 'SHOW_AUTOPLAN':
        resetRequestFn = ProfileSettings.resetPreferencesAutoplanner;
        break;
      case 'PREDICTION_PLAN':
        resetRequestFn = ProfileSettings.resetPreferencesPredictionPlanner;
        break;
      case 'PREDICTION_PLAN_IKTOS':
        resetRequestFn = ProfileSettings.resetPreferencesPredictionPlannerIktos;
        break;
      case 'RX2_SYNTHESIS_CONDITIONS_POSITION':
        resetRequestFn = ProfileSettings.resetPreferencesConditionsAndExamples;
        break;
      case 'PREFERRED_SUPPLIERS':
        resetRequestFn = ProfileSettings.resetPreferencesPreferredSuppliers;
        break;
      default:
        throw new Error('Unknown section to reset');
    }

    ProfileSettings.trackResetToDefault(sectionName);
    setIsLoading(true);

    resetRequestFn
      .call(null)
      .then(() => {
        // we need to reset by sections because another section might still contain unsaved changes
        const [, newEditUser] = resetSectionsToDefault(subsections || [sectionName]);
        setRxEditorOptions(buildRxEditorOptions(newEditUser));
        if (closeCallback) {
          closeCallback(newEditUser);
        }
      })
      .finally(() => {
        setIsLoading(false);
      })
      .catch(Logger.error);
  };

  const handleSectionClose = (section, closeCallback) => {
    const newEditUser = resetSectionsToOriginal([section]);
    setRxEditorOptions(buildRxEditorOptions(newEditUser));
    if (closeCallback) {
      closeCallback(newEditUser);
    }
  };

  const handlePreferenceChange = (path, value) => {
    setEditUser((prev) => setImmutable(prev, path, value));
  };

  return (
    <div
      className="rx-accordion rx-accordion--large profile-settings-accordion preferences-accordion"
      role="form"
      aria-label="Preference Settings">
      {isLoading && <SimpleSpinner id={spinnerId} />}
      <PreferencesPreferredSuppliersSection
        openSupplierPreferences={openSupplierPreferences}
        editUser={editUser}
        onSave={updatePreferencesSection}
        onClose={handleSectionClose}
        onChange={handlePreferenceChange}
        onReset={resetSections}
      />
      <PreferencesDefaultSection
        editUser={editUser}
        onSave={updatePreferencesSection}
        onClose={handleSectionClose}
        onChange={handlePreferenceChange}
        rxEditorOptions={rxEditorOptions}
        onReset={resetSections}
      />
      <PreferencesSynthesisPlannerSection
        onSave={updatePreferencesSection}
        onClose={handleSectionClose}
        onChange={handlePreferenceChange}
        onReset={resetSections}
        editUser={editUser}
      />
      <PreferencesResultsPerPageSection
        onSave={updatePreferencesSection}
        onClose={handleSectionClose}
        onChange={handlePreferenceChange}
        editUser={editUser}
      />
      <PreferencesTextAndContrastSection
        onSave={updatePreferencesSection}
        onClose={handleSectionClose}
        onChange={handlePreferenceChange}
        editUser={editUser}
      />
    </div>
  );
}

export default Preferences;
